import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  formatPlanDate,
  PLAN_LABEL,
  UPGRADE_PATH,
  upgradeGainsMessage,
  upgradePath,
} from '../../components/plan/planLabels';
import { usePlan } from '../../components/plan/usePlan';
import type { BillingSubscriptionRead, EntitlementsRead, PlanLimitKey } from '../../types/api';
import {
  billingErrorMessage,
  cancelSubscription,
  isLiveSubscription,
  openPortal,
  resumeSubscription,
} from '../billing/billingApi';
import { useBilling } from '../billing/useBilling';
import { useAccount } from '../onboarding/useAccount';

/** The caps the card lists, in the user's words. The rest show where they bite. */
const LISTED_LIMITS: readonly { key: PlanLimitKey; label: string }[] = [
  { key: 'posts', label: 'Posts per month' },
  { key: 'brands', label: 'Brands' },
  { key: 'team_members', label: 'Team members' },
];

/** Where the plan comes from, in one line. */
function sourceLine(entitlements: EntitlementsRead): string {
  const expires = entitlements.plan_expires_at;
  switch (entitlements.plan_source) {
    case 'trial':
      if (entitlements.trial_state === 'ended') {
        return expires ? `Trial ended ${formatPlanDate(expires)}` : 'Trial ended';
      }
      return expires ? `Trial ends ${formatPlanDate(expires)}` : 'Trial';
    case 'manual':
      return expires
        ? `Set by Autopostmate until ${formatPlanDate(expires)}`
        : 'Set by Autopostmate';
    case 'paddle':
      return 'Paid subscription';
  }
}

/** Free, or a trial that ran out (Free on the wire): the card offers the next plan. */
function offersUpgrade(entitlements: EntitlementsRead): boolean {
  return entitlements.plan === 'free' || entitlements.trial_state === 'ended';
}

/**
 * The account's plan: its name, where it comes from, and the three caps a
 * person decides on. On Free or after the trial, one upgrade line saying what
 * the next plan adds. For the owner of a Paddle subscription, while billing
 * is on, the billing lines and controls (`PlanBilling`).
 *
 * An unknown plan (still loading, or the read failed) says so and blocks
 * nothing: the backend enforces the plan, this card only reports it.
 */
export function PlanCard() {
  const { entitlements, loading, refresh } = usePlan();
  const { business } = useAccount();
  const billing = useBilling(business?.role === 'owner' && entitlements?.plan_source === 'paddle');
  const { hash } = useLocation();
  const cardRef = useRef<HTMLElement | null>(null);

  // Older links land on `/settings#plan`; the router does not scroll to a
  // fragment on its own.
  useEffect(() => {
    if (hash === '#plan') cardRef.current?.scrollIntoView({ block: 'start' });
  }, [hash]);

  return (
    <section className="wizard settings-card" id="plan" ref={cardRef}>
      <div className="settings-card__head">
        <h2 className="wizard__title">Plan and billing</h2>
        {entitlements ? (
          <p className="settings-card__lede">
            <span className="plan-card__name">{PLAN_LABEL[entitlements.plan]}</span>
            <span className="plan-card__source">{sourceLine(entitlements)}</span>
          </p>
        ) : null}
      </div>

      {entitlements ? (
        <>
          <dl className="spec-sheet">
            {LISTED_LIMITS.map(({ key, label }) => {
              const value = entitlements.limits[key];
              return (
                <div className="spec-sheet__row" key={key}>
                  <dt className="spec-sheet__term">{label}</dt>
                  <dd className="spec-sheet__value">
                    {value === null ? 'Unlimited' : value.toLocaleString()}
                  </dd>
                </div>
              );
            })}
          </dl>
          {billing.subscription && isLiveSubscription(billing.subscription.status) ? (
            <PlanBilling
              subscription={billing.subscription}
              onChange={(fresh) => {
                billing.setSubscription(fresh);
                refresh();
              }}
            />
          ) : null}
          <PlanUpgradeLine entitlements={entitlements} />
        </>
      ) : loading ? (
        <p className="kb-section__status" role="status">
          Reading your plan
        </p>
      ) : (
        <div className="plan-card__unknown">
          <p className="field__hint">
            Your plan could not be read just now. Nothing is locked because of it.
          </p>
          <button className="button button--ghost" type="button" onClick={refresh}>
            Try again
          </button>
        </div>
      )}
    </section>
  );
}

/** The card's upgrade line on Free or an ended trial; nothing otherwise. */
function PlanUpgradeLine({ entitlements }: { entitlements: EntitlementsRead }) {
  if (!offersUpgrade(entitlements)) return null;
  const message = upgradeGainsMessage(entitlements);
  if (!message) return null;

  return (
    <p className="upsell" role="note">
      <span className="upsell__message">{message}</span>{' '}
      <Link className="upsell__link" to={upgradePath(entitlements.next_plan)}>
        Upgrade
      </Link>
    </p>
  );
}

const INTERVAL_LABEL = { month: 'Monthly', year: 'Yearly' } as const;

/**
 * The owner's live Paddle subscription: interval, renewal or end date, a
 * discount chip, a stamp-red line while a payment is failing, and the four
 * controls. Change plan is the upgrade page; card and invoices are Paddle's
 * portal (new tab); Cancel runs to the end of the period paid for, and Resume
 * takes it back.
 */
function PlanBilling({
  subscription,
  onChange,
}: {
  subscription: BillingSubscriptionRead;
  onChange: (fresh: BillingSubscriptionRead) => void;
}) {
  const [busy, setBusy] = useState<'cancel' | 'resume' | 'portal' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const endsAt = subscription.scheduled_cancel_at;
  const renewsAt = subscription.current_period_end;
  const planName = subscription.plan ? PLAN_LABEL[subscription.plan] : 'your plan';

  async function run(kind: 'cancel' | 'resume', call: () => Promise<BillingSubscriptionRead>) {
    setBusy(kind);
    setError(null);
    try {
      onChange(await call());
      setIsConfirming(false);
    } catch (caught) {
      setError(billingErrorMessage(caught, 'That did not go through. Try again.'));
    } finally {
      setBusy(null);
    }
  }

  async function managePayment() {
    setBusy('portal');
    setError(null);
    try {
      await openPortal();
    } catch (caught) {
      setError(billingErrorMessage(caught, 'The billing portal did not open. Try again.'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="plan-card__billing">
      <p className="plan-card__billing-line">
        {subscription.interval ? <span>{INTERVAL_LABEL[subscription.interval]}</span> : null}
        {endsAt ? (
          <span>Ends {formatPlanDate(endsAt)}</span>
        ) : renewsAt ? (
          <span>Renews {formatPlanDate(renewsAt)}</span>
        ) : null}
        {subscription.discount_code ? (
          <span className="badge badge--neutral">{subscription.discount_code}</span>
        ) : null}
      </p>

      {subscription.status === 'past_due' ? (
        <p className="form__error" role="alert">
          Payment failed. Update your card.
        </p>
      ) : null}

      {error && !isConfirming ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="plan-card__actions">
        <Link className="button button--ghost" to={UPGRADE_PATH}>
          Change plan
        </Link>
        <button
          className="button button--ghost"
          type="button"
          disabled={busy !== null}
          onClick={() => void managePayment()}
        >
          {busy === 'portal' ? 'Opening' : 'Manage payment'}
        </button>
        {endsAt ? (
          <button
            className="button button--ghost"
            type="button"
            disabled={busy !== null}
            onClick={() => void run('resume', resumeSubscription)}
          >
            {busy === 'resume' ? 'Resuming' : 'Resume'}
          </button>
        ) : (
          <button
            className="button button--ghost"
            type="button"
            disabled={busy !== null}
            onClick={() => {
              setError(null);
              setIsConfirming(true);
            }}
          >
            Cancel
          </button>
        )}
      </div>

      {isConfirming ? (
        <ConfirmDialog
          title="Cancel your subscription?"
          message={`You keep ${planName}${renewsAt ? ` until ${formatPlanDate(renewsAt)}` : ' until the period ends'}, then the account moves to Free. Nothing is deleted, and you can resume any time before then.`}
          confirmLabel="Cancel subscription"
          busyLabel="Cancelling"
          dismissLabel="Keep it"
          isBusy={busy === 'cancel'}
          error={error}
          onConfirm={() => void run('cancel', cancelSubscription)}
          onDismiss={() => setIsConfirming(false)}
        />
      ) : null}
    </div>
  );
}
